import React, { useState, useEffect } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, ExternalLink } from 'react-feather';

const ReportedIssues = ({ chatbotId }) => {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedIssue, setExpandedIssue] = useState(null);

    useEffect(() => {
        if (!chatbotId || chatbotId === 'all') return;

        const fetchIssues = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/api/issues/${chatbotId}`);
                if (response.ok) {
                    const data = await response.json();
                    setIssues(data.issues || []);
                }
            } catch (error) {
                console.error('Error fetching filtered issues:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchIssues();
    }, [chatbotId]);

    if (!chatbotId || chatbotId === 'all') {
        return null; // Don't show for "All Chatbots" view yet
    }

    if (loading) return <div className="p-4 text-gray-500">Loading issues...</div>;

    if (issues.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No Issues Reported</h3>
                <p className="text-gray-500 mt-2">Your users haven't reported any visual issues yet.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-indigo-600" />
                        Reported Issues
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Visual bugs reported by users via Magic Wand</p>
                </div>
                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                    {issues.length} Open
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 font-medium text-gray-500">Date</th>
                            <th className="px-6 py-3 font-medium text-gray-500">Element</th>
                            <th className="px-6 py-3 font-medium text-gray-500">User Message</th>
                            <th className="px-6 py-3 font-medium text-gray-500">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {issues.map((issue) => (
                            <React.Fragment key={issue.id}>
                                <tr
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => setExpandedIssue(expandedIssue === issue.id ? null : issue.id)}
                                >
                                    <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                                        {new Date(issue.timestamp).toLocaleDateString()}
                                        <br />
                                        <span className="text-xs">{new Date(issue.timestamp).toLocaleTimeString()}</span>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900">
                                        <code className="bg-gray-100 px-2 py-1 rounded text-xs border border-gray-200">
                                            {issue.tagName}{issue.id ? `#${issue.id}` : ''}
                                        </code>
                                    </td>
                                    <td className="px-6 py-4 text-gray-600 max-w-xs truncate">
                                        {issue.userMessage}
                                    </td>
                                    <td className="px-6 py-4 text-indigo-600">
                                        {expandedIssue === issue.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </td>
                                </tr>
                                {expandedIssue === issue.id && (
                                    <tr className="bg-gray-50">
                                        <td colSpan="4" className="px-6 py-4 border-b border-gray-200">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Selector</h4>
                                                    <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-xs">
                                                        {issue.selector}
                                                    </pre>
                                                </div>
                                                <div>
                                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Details</h4>
                                                    <ul className="text-sm space-y-1 text-gray-600">
                                                        <li><strong>Tag:</strong> {issue.tagName}</li>
                                                        <li><strong>Classes:</strong> {issue.className || 'None'}</li>
                                                        <li><strong>Source:</strong> {issue.src ? <a href={issue.src} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-1 inline-flex">Link <ExternalLink size={10} /></a> : 'N/A'}</li>
                                                        <li><strong>Href:</strong> {issue.href ? <a href={issue.href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-1 inline-flex">Link <ExternalLink size={10} /></a> : 'N/A'}</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">HTML Snippet</h4>
                                                <pre className="bg-gray-100 text-gray-700 p-3 rounded-lg overflow-x-auto text-xs border border-gray-200">
                                                    {issue.html}
                                                </pre>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ReportedIssues;
